import gql from 'graphql-tag'
import { graphQLBase } from '..'
import { noteAcknowledgementFragment } from 'remote/activityRegistry'
import { print } from 'graphql/language/printer'
export const noteBaseAttributesFragment = gql`
  fragment noteBaseAttributes on Note {
    id
    __typename
    createdAt
    createdBy
    updatedAt
    title
    channelId
    archivedAt
    activityCount
    lastHistoryVersion
    lastActivityBy
    lastActivityAt
    lastActivityType
    publicShareToken
    publiclySharedAt
    publiclySharedBy
    madePubliclyEditableAt
    madePubliclyEditableBy
    shareLink
    listPosition
    otType
    parentNote {
      id
    }
    iconShape
    iconColor
    hasChildren
    isLocked
  }
`

export const publicNoteBaseAttributesFragment = gql`
  fragment publicNoteBaseAttributes on PublicNote {
    id
    __typename
    title
    channelId
    publicShareToken
    publiclySharedAt
    publiclySharedBy
    listPosition
    otType
    iconShape
    iconColor
    hasChildren
    parentNote {
      id
    }
  }
`

export const noteAttrsForListsFragment = gql`
  fragment noteAttrsForLists on Note {
    ...noteBaseAttributes
    activityAcknowledgement {
      ...noteAcknowledgementAttrs
    }
  }
  ${noteBaseAttributesFragment}
  ${noteAcknowledgementFragment}
`

export const noteAttrsForListsWithGoodSidebarFragment = gql`
  fragment noteAttrsForLists on Note {
    id
    __typename

    # List display
    title
    channelId
    archivedAt
    iconShape
    iconColor
    listPosition
    hasChildren
    parentNote {
      id
    }

    # Content
    otType

    # Controls
    publiclySharedAt
    shareLink

    # Activity
    lastActivityBy
    lastActivityAt
    activityAcknowledgement {
      ...noteAcknowledgementAttrs
    }
  }
  ${noteAcknowledgementFragment}
`

export const publicNoteAttrsForListsFragment = gql`
  fragment publicNoteAttrsForLists on PublicNote {
    ...publicNoteBaseAttributes
  }
  ${publicNoteBaseAttributesFragment}
`

export const noteAttrsForSubscriptionsFragment = gql`
  fragment noteAttrsForSubscriptions on Note {
    ...noteBaseAttributes
  }
  ${noteBaseAttributesFragment}
`

export const noteAttrsFragment = gql`
  fragment noteAttrs on Note {
    ...noteBaseAttributes
    activityAcknowledgement {
      ...noteAcknowledgementAttrs
    }
  }
  ${noteBaseAttributesFragment}
  ${noteAcknowledgementFragment}
`

export const templateNoteAttrsFragment = gql`
  fragment templateNoteAttrs on Note {
    __typename
    id
    title
    lastActivityBy
    lastActivityAt
    lastActivityType
  }
`

export const createNoteMutation = gql`
  mutation CreateNote(
    $title: String!
    $channelId: ID!
    $parentNoteId: ID
    $snapshot: Delta!
  ) {
    createNote(
      title: $title
      channelId: $channelId
      parentNoteId: $parentNoteId
      snapshot: $snapshot
    ) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`

const NotesAPI = {
  createNote: graphQLBase(
    print(createNoteMutation),
    ({ createNote }) => createNote
  ),

  lockNoteForEdition: gql`
    mutation lockNoteForEdition($id: ID!) {
      lockNoteForEdition(id: $id) {
        id
        isLocked
        __typename
      }
    }
  `,

  unlockNoteForEdition: gql`
    mutation unlockNoteForEdition($id: ID!) {
      unlockNoteForEdition(id: $id) {
        id
        isLocked
        __typename
      }
    }
  `,

  updateNote: graphQLBase(
    `mutation UpdateNote($id: ID!, $title: String!) {
      updateNote(id: $id, title: $title) {
        id
        __typename
        title
        createdAt
        updatedAt
        channelId
      }
    }`,
    ({ updateNote }) => updateNote
  ),

  duplicateNote: gql`
    mutation duplicateNote($input: DuplicateNoteInput!) {
      duplicateNote(input: $input) {
        ...noteAttrs
      }
    }
    ${noteAttrsFragment}
  `,

  deleteNote: graphQLBase(
    `mutation DeleteNote($id: ID!) {
      deleteNote(id: $id) {
        id
        __typename
      }
    }`,
    ({ deleteNote }) => deleteNote
  ),

  shareNoteToEmail: graphQLBase(
    `mutation ShareNoteToEmail($input: ShareNoteToEmailInput!) {
      shareNoteToEmail(input: $input) {
        success
      }
    }
    `,
    ({ shareNoteToEmail }) => shareNoteToEmail
  ),

  deleteNotes: graphQLBase(
    `mutation DeleteNotes($ids: [ID!]!) {
      deleteNotes(ids: $ids) {
        id
        __typename
      }
    }`,
    ({ deleteNotes }) => deleteNotes
  ),

  archiveNote: graphQLBase(
    `mutation ArchiveNote($id: ID!) {
      archiveNote(id: $id) {
        id
        __typename
      }
    }`,
    ({ archiveNote }) => archiveNote
  ),

  archiveNotes: graphQLBase(
    `mutation ArchiveNotes($ids: [ID!]!) {
      archiveNotes(ids: $ids) {
        id
        __typename
      }
    }`,
    ({ archiveNotes }) => archiveNotes
  ),

  unarchiveNote: graphQLBase(
    `mutation UnarchiveNote($id: ID!) {
      unarchiveNote(id: $id) {
        id
        __typename
      }
    }`,
    ({ unarchiveNote }) => unarchiveNote
  ),

  unarchiveNotes: graphQLBase(
    `mutation UnarchiveNotes($ids: [ID!]!) {
      unarchiveNotes(ids: $ids) {
        id
        __typename
      }
    }`,
    ({ unarchiveNotes }) => unarchiveNotes
  ),

  moveNotesTo: graphQLBase(
    `mutation MoveNotesTo($ids: [ID!]!, $channelId: ID!, $parentNoteId: ID, $position: Float) {
      moveNotesTo(ids: $ids, channelId: $channelId, parentNoteId: $parentNoteId, position: $position) {
        id
        __typename
        channelId
        listPosition
        parentNote {
          id
        }
      }
    }`,
    ({ moveNotesTo }) => moveNotesTo
  ),
}

export default NotesAPI

export const fetchAllTemplateNotes = gql`
  query fetchAllTemplateNotes($pagination: PaginationInput) {
    showAllTemplateNotes(
      pagination: $pagination
      excludeCollectionNotes: false
    ) {
      totalCount
      edges {
        cursor
        node {
          ...templateNoteAttrs
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  ${templateNoteAttrsFragment}
`

export const applyTemplateOnNote = gql`
  mutation applyTemplateOnNote($sourceNoteId: ID!, $destinationNoteId: ID!) {
    applyTemplateOnNote(
      sourceNoteId: $sourceNoteId
      destinationNoteId: $destinationNoteId
    ) {
      ...noteAttrsForLists
    }
  }
  ${noteAttrsForListsFragment}
`

export const deleteTemplate = gql`
  mutation deleteTemplate($id: ID!) {
    deleteTemplate(id: $id) {
      __typename
      id
    }
  }
`

export const createTemplate = gql`
  mutation createTemplate($input: CreateTemplateInput!) {
    createTemplate(input: $input) {
      ...noteBaseAttributes
    }
  }
  ${noteBaseAttributesFragment}
`

export const createNoteFromTemplate = gql`
  mutation createNoteFromTemplate($input: CreateNoteFromTemplateInput!) {
    createNoteFromTemplate(input: $input) {
      ...noteAttrsForLists
    }
  }
  ${noteAttrsForListsFragment}
`

export const notesArchivedSubscription = gql`
  subscription notesArchived($organizationId: ID!) {
    notesArchived(organizationId: $organizationId) {
      ids
    }
  }
`

export const notesUnarchivedSubscription = gql`
  subscription notesUnarchived($organizationId: ID!) {
    notesUnarchived(organizationId: $organizationId) {
      ids
    }
  }
`

export const notesDeletedSubscription = gql`
  subscription notesDeleted($organizationId: ID!) {
    notesDeleted(organizationId: $organizationId) {
      ids
    }
  }
`

export const notesMovedSubscription = gql`
  subscription notesMoved($organizationId: ID!) {
    notesMoved(organizationId: $organizationId) {
      ids
      channelId
      parentNoteId
    }
  }
`

export const fetchAllBookmarkedNotes = gql`
  query ShowAllBookmarkedNotes {
    showAllBookmarkedNotes {
      ...noteAttrsForLists
    }
  }
  ${noteAttrsForListsFragment}
`

export const updateNote = gql`
  mutation updateNote($id: ID!, $title: String!) {
    updateNote(id: $id, title: $title) {
      ...noteAttrsForLists
    }
  }
  ${noteAttrsForListsFragment}
`

export const updateNotePositionInChannel = gql`
  mutation updateNotePositionInChannel(
    $channelId: ID!
    $noteId: ID!
    $position: Float!
    $sectionKey: ID!
  ) {
    updateNotePositionInChannel(
      channelId: $channelId
      noteId: $noteId
      position: $position
      sectionKey: $sectionKey
    ) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`

export const publiclyShareNote = gql`
  mutation PubliclyShareNote($id: ID!) {
    publiclyShareNote(id: $id) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`

export const publiclyUnshareNote = gql`
  mutation PubliclyUnshareNote($id: ID!) {
    publiclyUnshareNote(id: $id) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`

export const makePublicNoteEditable = gql`
  mutation MakePublicNoteEditable($id: ID!) {
    makePublicNoteEditable(id: $id) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`

export const makePublicNoteNonEditable = gql`
  mutation MakePublicNoteNonEditable($id: ID!) {
    makePublicNoteNonEditable(id: $id) {
      ...noteAttrs
    }
  }
  ${noteAttrsFragment}
`
